import React, {useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import {MODES} from '../state/informeModel';
import {clientNameOf, selectableClients} from '../state/clientList';
import {backToClientFor} from '../../data_explorer/backToClient';
import {anchorAttr, isGuideUi} from '../../../guides/anchors';

/**
 * La barra superior del editor (mockup): selector de cliente, chip del informe (con las pills
 * EN PORTAL / FOTO·fecha y el punto naranja de "sin guardar"), «Abrir informe» y el split
 * «Guardar informe» con sus dos grupos — el informe interno de la agencia y el portal del cliente.
 *
 * Con `inClientFicha` (la solapa Informes de la ficha, 2.1) no van ni el selector de cliente ni
 * «Volver a…»: el cliente lo dan la miga y las solapas de arriba, y para cambiarlo está la miga
 * (opción A del mockup del 02/10, card 7yIKxqZj). La barra arranca directo en el informe.
 */
export function InformeHeaderBar({
    agencyClients, agencyClientId, onClientChange, cameFromClient, inClientFicha = false,
    informe, informeDirty, snapshotAt,
    onOpenPicker, onSave, onSaveAsNew, onDuplicate, onDownloadPdf,
    onPublishToggle, onRefreshSnapshot, onPreviewAsClient, pdfLoading, pdfUnavailable = false,
}) {
    const {t} = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [clientsOpen, setClientsOpen] = useState(false);
    const menuRef = useRef(null);
    const clientsRef = useRef(null);

    useEffect(() => {
        if (!menuOpen) return undefined;
        // La tarjeta de una guía no cuenta como «afuera»: la guía de compartir explica este menú con él
        // abierto (review C-apr-guias-02).
        const onClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target) && !isGuideUi(e.target)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [menuOpen]);

    useEffect(() => {
        if (!clientsOpen) return undefined;
        const onClickOutside = (e) => { if (clientsRef.current && !clientsRef.current.contains(e.target)) setClientsOpen(false); };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [clientsOpen]);

    // Las OPCIONES se filtran por activo; el nombre del elegido sale de la lista completa, para
    // que un informe guardado sobre un cliente dado de baja siga diciendo cuál es.
    const clientOptions = selectableClients(agencyClients);
    const clientName = clientNameOf(agencyClients, agencyClientId);
    const saved = !!(informe && informe.id);
    const isSnapshot = !!(informe && informe.mode === MODES.SNAPSHOT);
    // `guideAnchor`: el resultado de `anchorAttr(...)` del ítem al que apunta una guía.
    const item = (label, onClick, {disabled = false, testId, guideAnchor = {}} = {}) => (
        <button type="button" className="inf-dd-item" role="menuitem" disabled={disabled} data-testid={testId}
                onClick={() => { setMenuOpen(false); onClick(); }} {...guideAnchor}>{label}</button>
    );

    /**
     * La vuelta a la ficha del cliente (card 1Ft2oNtA) — el mismo criterio que en el Explorador.
     *
     * Va también acá y no sólo allá porque **qué pantalla abre la pestaña «Explorador de datos» de
     * la ficha lo decide la modalidad de la agencia**: con `REPORT_WITH_TEMPLATES` aterriza en
     * Informes, y ésa es justamente la modalidad de la agencia desde la que Pablo reportó el tema.
     * Arreglarlo sólo en `/data-explorer` habría dejado el arreglo en la pantalla que él no ve.
     */
    const back = inClientFicha ? null : backToClientFor({cameFromClient, agencyClientId, agencyClients});

    return (
        <div className="inf-header" data-testid="informes-header">
            {back && (
                <Link className="inf-back" to={back.path} data-testid="back-to-client">
                    <span className="inf-back-caret" aria-hidden="true">‹</span>
                    {t('informes.header.back_to_client', {client: back.name,
                                                          interpolation: {escapeValue: false}})}
                </Link>
            )}
            {/* Chip + menú, no un `<select>`: el nativo se dimensiona por la opción más larga, así
                que un cliente con nombre kilométrico estiraba la miga entera aunque el elegido se
                llamara «Dashbo Ads». Sin rótulo «CLIENTE» delante — va en `aria-label`. */}
            {!inClientFicha && (
                <>
                    <div className="inf-client-anchor" ref={clientsRef}>
                        <button type="button" className="inf-client-chip" aria-haspopup="menu" aria-expanded={clientsOpen}
                                aria-label={t('informes.header.client', 'Cliente')} data-testid="client-select"
                                onClick={() => setClientsOpen(!clientsOpen)}>
                            <span className="inf-client-name">{clientName || t('informes.header.pick_client', 'Elegir cliente')}</span>
                            <span className="inf-caret">▾</span>
                        </button>
                        {clientsOpen && (
                            <div className="inf-dd -left" role="menu" data-testid="client-menu">
                                {clientOptions.map((c) => (
                                    <button key={c.id} type="button" role="menuitem"
                                            className={`inf-dd-item ${String(c.id) === String(agencyClientId) ? '-current' : ''}`}
                                            onClick={() => { setClientsOpen(false); onClientChange(String(c.id)); }}>
                                        {c.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <span className="inf-crumb-sep" aria-hidden="true">›</span>
                </>
            )}

            <button type="button" className={`inf-informe-chip ${informeDirty ? '-dirty' : ''}`} onClick={onOpenPicker}
                    title={t('informes.header.open', 'Abrir informe')} data-testid="informe-chip"
                    {...anchorAttr('informes_informe_chip')}>
                <span className="inf-ci-label">{t('informes.header.informe_label', 'Informe')}</span>
                <span className="inf-informe-name">{saved ? informe.name : t('informes.header.new_informe', 'Informe nuevo')}</span>
                {saved && informe.is_published && <span className="inf-pill -portal">{t('informes.pill.published', 'EN PORTAL')}</span>}
                {saved && isSnapshot && (
                    <span className="inf-pill -snapshot">
                        {t('informes.pill.snapshot', 'FOTO')}{snapshotAt ? ` · ${moment(snapshotAt).format('DD/MM')}` : ''}
                    </span>
                )}
                {informeDirty && <span className="inf-dot" aria-label={t('informes.header.unsaved', 'Cambios sin guardar')}/>}
                <span className="inf-caret">▾</span>
            </button>

            <span className="inf-spacer"/>

            {/* «Guardado» / «Sin guardar» junto al botón, como el mockup; el punto naranja del
                chip queda como segunda señal. */}
            <span className={`inf-save-status ${informeDirty ? '-dirty' : ''}`} data-testid="save-status">
                {informeDirty
                    ? t('informes.header.unsaved_short', 'Sin guardar')
                    : <>{'✓'} {t('informes.header.saved', 'Guardado')}</>}
            </span>

            <div className="inf-split-wrap" ref={menuRef}>
                <span className="inf-split -informe">
                    <button type="button" className="inf-split-main" onClick={onSave} data-testid="save-informe"
                            {...anchorAttr('informes_save')}>
                        {t('informes.header.save', 'Guardar informe')}
                    </button>
                    <button type="button" className="inf-split-caret" aria-haspopup="menu" aria-expanded={menuOpen}
                            aria-label={t('informes.header.save_options', 'Opciones de guardado')} onClick={() => setMenuOpen(!menuOpen)}
                            {...anchorAttr('informes_save_options')}>▾</button>
                </span>
                {menuOpen && (
                    <div className="inf-dd" role="menu" data-testid="save-informe-menu">
                        <div className="inf-dd-sec">
                            <div className="inf-dd-sec-head">{t('informes.header.group_internal', 'Informe interno de la agencia')}</div>
                            {item(t('informes.header.save', 'Guardar informe'), onSave)}
                            {item(t('informes.header.save_as_new', 'Guardar como informe nuevo…'), onSaveAsNew, {disabled: !saved})}
                            {item(t('informes.header.duplicate', 'Duplicar en otro cliente…'), onDuplicate, {disabled: !saved})}
                        </div>
                        <div className="inf-dd-sec -portal">
                            <div className="inf-dd-sec-head">{t('informes.header.group_portal', 'Portal del cliente')}</div>
                            {item(
                                informe && informe.is_published
                                    ? t('informes.header.unpublish', 'Quitar del portal')
                                    : t('informes.header.publish', 'Asociar al portal'),
                                onPublishToggle,
                                {disabled: !saved, testId: 'publish-toggle', guideAnchor: anchorAttr('informes_publish')},
                            )}
                            {isSnapshot && item(t('informes.header.refresh_snapshot', 'Actualizar la foto'), onRefreshSnapshot, {disabled: !saved})}
                            {item(t('informes.header.preview_as_client', 'Ver informe como cliente'), onPreviewAsClient,
                                  {disabled: !(saved && informe.is_published)})}
                        </div>
                    </div>
                )}
            </div>

            {/* Descargar como botón suelto (mockup `.icon-btn`): es la acción de una pantalla que
                se mira, no una variante de guardar, y adentro del menú no se encontraba. */}
            {onDownloadPdf && (
                /* Armar el PDF son ~50 s con el hilo principal tomado a ratos (medido el 07/09 sobre
                   un informe de 7 paneles): sin decirlo, el clic se lee como que la app se colgó y
                   se vuelve a apretar. Deshabilitado + «Armando el PDF…», con `aria-live` para que
                   también se anuncie. */
                /* Y con el informe caído tampoco se deja bajar: el exportador toma SÓLO los refs de
                   los paneles, así que ni la franja del error ni el aviso de arriba viajan al
                   archivo. Antes salían doce bloques diciendo «Sin datos para este período» —falso,
                   pero afirmativo— y ahora doce «No disponible», que es honesto y no dice NADA. El
                   PDF es justo el artefacto que llega al cliente sin la pantalla al lado
                   (review R-reports-fe-08). */
                <button type="button" className={`inf-icon-btn ${pdfLoading ? '-busy' : ''}`}
                        onClick={onDownloadPdf} data-testid="download-pdf"
                        disabled={!!pdfLoading || !!pdfUnavailable}
                        title={pdfLoading
                            ? t('informes.header.download_pdf_busy', 'Armando el PDF… puede tardar un minuto.')
                            : (pdfUnavailable
                                ? t('informes.header.download_pdf_unavailable', 'El informe no se pudo calcular: no hay nada que descargar.')
                                : t('informes.header.download_pdf', 'Descargar PDF'))}
                        aria-label={pdfLoading
                            ? t('informes.header.download_pdf_busy', 'Armando el PDF… puede tardar un minuto.')
                            : (pdfUnavailable
                                ? t('informes.header.download_pdf_unavailable', 'El informe no se pudo calcular: no hay nada que descargar.')
                                : t('informes.header.download_pdf', 'Descargar PDF'))}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 3v12M6 11l6 6 6-6M4 21h16"/>
                    </svg>
                </button>
            )}
            {/* La región va SIEMPRE montada y sólo cambia el texto: un `aria-live` que nace junto
                con su contenido no se anuncia — los lectores de pantalla leen los CAMBIOS dentro de
                una región que ya estaba presente. */}
            <span className="inf-pdf-busy" role="status" aria-live="polite">
                {pdfLoading ? t('informes.header.download_pdf_busy', 'Armando el PDF… puede tardar un minuto.') : ''}
            </span>
        </div>
    );
}
